/**
 * Canonical unit handling for Finlytics ratio tables and charts.
 *
 * API metadata mixes '%', 'x', '₹ Cr', '₹', 'pp', 'days', and registry
 * kinds ('percentage', 'multiple', 'currency', 'perShare'). Charts must
 * never treat a margin / return as crores.
 */

const PERCENTAGE_RATIO_NAMES = new Set([
    'grossProfitMargin',
    'ebitdaMargin',
    'operatingProfitMargin',
    'netProfitMargin',
    'standardRoic',
    'operatingRoic',
    'wacc',
    'returnOnAssets',
    'returnOnEquity',
    'returnOnCapitalEmployed',
    'dividendPayoutRatio',
    'fcfMargin',
    'capexToCfoPercent',
    'capexToSalesPercent',
    'cashRoic',
    'debtToAssets',
    'nwcPctSales',
    'cashConversionSales',
    'taxRate',
    'salesGrowth',
    'compoundedSalesGrowth',
    'compoundedProfitGrowth',
    'stockPriceCagr',
]);

const PP_RATIO_NAMES = new Set(['roicWaccSpread']);

const MULTIPLE_RATIO_NAMES = new Set([
    'interestCoverage',
    'cfoToNetProfit',
    'cfoToEbit',
    'cfoToEbitda',
    'debtRepaymentToCfo',
    'dividendsToCfo',
    'fcfToNetProfit',
    'cfoToDebt',
    'cashRatio',
    'cashFlowCoverageRatio',
    'debtToEquity',
    'currentRatio',
    'quickRatio',
    'assetTurnover',
    'inventoryTurnover',
    'receivablesTurnover',
    'cfoToPat',
]);

const CURRENCY_RATIO_NAMES = new Set([
    'fcfAbsolute',
    'ownerEarnings',
    'workingCapitalDelta',
    'tradeNWC',
    'workingCapitalChange',
]);

const PER_SHARE_RATIO_NAMES = new Set(['earningsPerShare', 'eps']);

const DAYS_RATIO_NAMES = new Set(['dso', 'dio', 'dpo', 'ccc']);

/**
 * @param {unknown} unit
 * @returns {'percentage'|'percentagePoints'|'multiple'|'currencyCrore'|'perShare'|'days'|'boolean'|'score'|'unknown'}
 */
export function classifyFinancialUnit(unit) {
    if (unit == null) return 'unknown';
    const raw = String(unit).trim();
    if (!raw) return 'unknown';
    const lower = raw.toLowerCase();

    if (lower === 'pp' || lower === 'percentagepoints' || lower === 'percentage points') {
        return 'percentagePoints';
    }
    if (raw === '%' || lower === 'percentage' || lower === 'percent' || raw.includes('%')) {
        return 'percentage';
    }
    if (lower === 'x' || lower === 'multiple' || lower === 'times') {
        return 'multiple';
    }
    if (lower === 'boolean') return 'boolean';
    if (lower === 'score') return 'score';
    if (lower === 'd' || lower === 'day' || lower === 'days') return 'days';
    if (
        lower === 'pershare'
        || lower === 'per share'
        || raw === '₹'
        || lower === 'rs'
        || lower === 'inr'
        || lower === 'rupee'
        || lower === 'rupees'
    ) {
        return 'perShare';
    }
    if (
        lower === 'currency'
        || lower === 'cr'
        || lower.includes('crore')
        || /\bcr\b/i.test(raw)
    ) {
        return 'currencyCrore';
    }
    return 'unknown';
}

function inferKindFromName(name, displayName) {
    const key = String(name || '').trim();
    if (PP_RATIO_NAMES.has(key)) return 'percentagePoints';
    if (PERCENTAGE_RATIO_NAMES.has(key)) return 'percentage';
    if (MULTIPLE_RATIO_NAMES.has(key)) return 'multiple';
    if (CURRENCY_RATIO_NAMES.has(key)) return 'currencyCrore';
    if (PER_SHARE_RATIO_NAMES.has(key)) return 'perShare';
    if (DAYS_RATIO_NAMES.has(key)) return 'days';

    const hay = `${key} ${displayName || ''}`.toLowerCase();
    if (!hay.trim()) return 'unknown';
    if (/\bspread\b|roic\s*[-−]\s*wacc|minus wacc/.test(hay)) return 'percentagePoints';
    if (/\bmargin\b|\broe\b|\broa\b|\broce\b|\broic\b|\bwacc\b|payout|cagr|growth|yield/.test(hay)) {
        return 'percentage';
    }
    if (/\bcoverage\b|\bturnover\b|debt to equity|current ratio|quick ratio|cash ratio/.test(hay)) {
        return 'multiple';
    }
    if (/\beps\b|per share/.test(hay)) return 'perShare';
    if (/\bdso\b|\bdio\b|\bdpo\b|\bccc\b|\bdays\b/.test(hay)) return 'days';
    return 'unknown';
}

/**
 * Resolve a display/chart unit kind from API metadata, with name fallback
 * when the unit is missing or encoding-garbled (e.g. "�" instead of "₹").
 *
 * @param {{ unit?: unknown, name?: string, displayName?: string }} [input]
 * @returns {string}
 */
export function resolveChartUnitKind(input = {}) {
    const classified = classifyFinancialUnit(input.unit);
    if (classified !== 'unknown') return classified;
    return inferKindFromName(input.name, input.displayName);
}

/**
 * True when the series should use a ratio-style axis (%, pp, or x),
 * not a crore currency axis.
 *
 * @param {unknown} unit
 * @returns {boolean}
 */
export function isDerivedChartUnit(unit) {
    const kind = classifyFinancialUnit(unit);
    return kind === 'percentage' || kind === 'percentagePoints' || kind === 'multiple';
}

/**
 * Unit kind to feed `parseMetricValue` (percentage-point vs numeric).
 *
 * @param {unknown} unit
 * @returns {'percentage'|'currency'}
 */
export function parseUnitForMetricValue(unit) {
    const kind = classifyFinancialUnit(unit);
    if (kind === 'percentage' || kind === 'percentagePoints') return 'percentage';
    return 'currency';
}

function formatNumber(value) {
    return Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

/**
 * Format a tick, tooltip, or badge value with the correct suffix.
 *
 * @param {number|null|undefined} value
 * @param {unknown} unit
 * @param {{ name?: string, displayName?: string }} [hints]
 * @returns {string}
 */
export function formatFinancialUnitValue(value, unit, hints = {}) {
    if (value == null || !Number.isFinite(Number(value))) return '';
    const n = Number(value);
    const formatted = formatNumber(n);
    const kind = resolveChartUnitKind({ unit, ...hints });
    switch (kind) {
        case 'percentage':
            return `${formatted}%`;
        case 'percentagePoints':
            return `${formatted} pp`;
        case 'multiple':
            return `${formatted}x`;
        case 'currencyCrore':
            return `${formatted} Cr`;
        case 'perShare':
            return `₹${formatted}`;
        case 'days':
            return `${formatted}d`;
        default:
            return formatted;
    }
}

/**
 * Compact unit label for table headers, e.g. `(%)` or `(₹ Cr)`.
 *
 * @param {unknown} unit
 * @param {{ name?: string, displayName?: string }} [hints]
 * @returns {string}
 */
export function formatRatioUnitLabel(unit, hints = {}) {
    const kind = resolveChartUnitKind({ unit, ...hints });
    switch (kind) {
        case 'percentage':
            return '%';
        case 'percentagePoints':
            return 'pp';
        case 'multiple':
            return 'x';
        case 'currencyCrore':
            return '₹ Cr';
        case 'perShare':
            return '₹';
        case 'days': {
            const rawDays = String(unit || '').trim().toLowerCase();
            return rawDays === 'd' ? 'd' : 'days';
        }
        case 'boolean':
            return 'Boolean';
        case 'score':
            return 'score';
        default: {
            const raw = unit == null ? '' : String(unit).trim();
            if (!raw || raw === '?' || raw === '\uFFFD') return '';
            return raw;
        }
    }
}

/**
 * Table caption: "Gross Profit Margin (%)".
 *
 * @param {{ displayName?: string, name?: string, unit?: unknown }} ratio
 * @returns {string}
 */
export function formatRatioNameWithUnit(ratio) {
    const label = ratio?.displayName || ratio?.name || '';
    const suffix = formatRatioUnitLabel(ratio?.unit, {
        name: ratio?.name,
        displayName: ratio?.displayName,
    });
    if (!label) return suffix;
    if (!suffix) return label;
    if (label.includes(`(${suffix})`)) return label;
    return `${label} (${suffix})`;
}

const RUPEES_PER_CRORE = 1e7;

/**
 * Company documents store marketCap in rupees. Header badges must show crores.
 *
 * @param {unknown} marketCapRupees
 * @returns {string}
 */
export function formatMarketCapFromRupees(marketCapRupees) {
    const rupees = Number(marketCapRupees);
    if (!Number.isFinite(rupees)) return '';
    const crores = rupees / RUPEES_PER_CRORE;
    const digits = Math.abs(crores) >= 100 ? 0 : 2;
    return `₹${crores.toLocaleString('en-IN', { maximumFractionDigits: digits })} Cr`;
}

/**
 * Format share count for header badges (e.g. 26,319,660 → "2.63 Cr").
 *
 * @param {unknown} shares
 * @returns {string}
 */
export function formatSharesOutstanding(shares) {
    const n = Number(shares);
    if (!Number.isFinite(n) || n <= 0) return '';
    const crores = n / RUPEES_PER_CRORE;
    const digits = Math.abs(crores) >= 100 ? 0 : 2;
    return `${crores.toLocaleString('en-IN', { maximumFractionDigits: digits })} Cr`;
}

